/**
 * FineImportWizard — Import IA d'un avis de contravention.
 * Drop PDF → Gemini extrait → preview + auto-match véhicule/conducteur → Apply.
 */
import { useState, useRef, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Upload, FileText, Sparkles, Loader2, Car, User, Check, X, AlertTriangle,
  ChevronLeft, UserPlus, Briefcase, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const PERSON_TYPE_META = {
  employee: { label: "Salarié", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  mandataire_social: { label: "Mandataire", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  conducteur_externe: { label: "Externe", cls: "bg-orange-50 text-orange-700 border-orange-200" },
};

export default function FineImportWizard({ open, onOpenChange, onCreated }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [detected, setDetected] = useState(null);
  const [notifyNow, setNotifyNow] = useState(false);
  const [drivers, setDrivers] = useState({ employee: [], mandataire_social: [], conducteur_externe: [] });
  const [vehicles, setVehicles] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const dropRef = useRef(null);

  const loadDrivers = async () => {
    try {
      const r = await api.get("/fleet/drivers");
      setDrivers(r.data.groups || { employee: [], mandataire_social: [], conducteur_externe: [] });
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    if (!open) return;
    setStep(1); setFile(null); setDetected(null); setNotifyNow(false);
    setSelectedEmployeeId(""); setSelectedVehicleId("");
    (async () => {
      try {
        const [v] = await Promise.all([api.get("/fleet/vehicles")]);
        setVehicles(v.data.vehicles || []);
      } catch (e) { /* silent */ }
      await loadDrivers();
    })();
  }, [open]);

  const allDrivers = [
    ...drivers.employee.map(d => ({ ...d, _group: "employee" })),
    ...drivers.mandataire_social.map(d => ({ ...d, _group: "mandataire_social" })),
    ...drivers.conducteur_externe.map(d => ({ ...d, _group: "conducteur_externe" })),
  ];
  const selectedDriver = allDrivers.find(d => d.id === selectedEmployeeId);

  const onFile = (f) => {
    if (!f) return;
    if (!["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp"]
          .includes(f.type)) {
      toast.error("Format non supporté (PDF, PNG, JPG uniquement)");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (>10 Mo)");
      return;
    }
    setFile(f);
  };

  const extract = async () => {
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/fleet/fines/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDetected(r.data);
      setSelectedVehicleId(r.data.matched_vehicle?.id || "");
      setSelectedEmployeeId(r.data.suggested_employee?.id
                              || r.data.matched_vehicle?.assigned_employee_id
                              || "");
      setStep(2);
    } catch (e) { toast.error(apiError(e)); }
    finally { setExtracting(false); }
  };

  const apply = async () => {
    if (!detected) return;
    setApplying(true);
    try {
      const r = await api.post("/fleet/fines/apply", {
        upload_id: detected.upload_id,
        vehicle_id: selectedVehicleId || null,
        assigned_employee_id: selectedEmployeeId || null,
        reference: detected.reference,
        natinf: detected.natinf,
        plate: detected.plate,
        infraction_date: detected.infraction_date,
        infraction_time: detected.infraction_time,
        location: detected.location,
        reason: detected.reason,
        amount_reduced: detected.amount_reduced,
        amount_normal: detected.amount_normal,
        amount_late: detected.amount_late,
        points_lost: detected.points_lost,
        payment_deadline: detected.payment_deadline,
        designation_deadline: detected.designation_deadline,
        issuer: detected.issuer,
        notify_driver_now: notifyNow,
      });
      toast.success(notifyNow
        ? "Amende créée · conducteur notifié"
        : "Amende créée");
      onCreated?.();
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setApplying(false); }
  };

  const canNotify = selectedEmployeeId
    && allDrivers.find(d => d.id === selectedEmployeeId)?.email;

  const onDriverCreated = async (newDriver) => {
    await loadDrivers();
    setSelectedEmployeeId(newDriver.id);
    setQuickCreateOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-slate-700" />
            Importer une amende — IA Gemini 3 Flash
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div
              ref={dropRef}
              onDragOver={(e) => { e.preventDefault(); dropRef.current?.classList.add("bg-blue-50", "border-blue-400"); }}
              onDragLeave={() => dropRef.current?.classList.remove("bg-blue-50", "border-blue-400")}
              onDrop={(e) => {
                e.preventDefault();
                dropRef.current?.classList.remove("bg-blue-50", "border-blue-400");
                onFile(e.dataTransfer.files?.[0]);
              }}
              className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center transition"
              data-testid="fine-drop-zone"
            >
              <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="text-sm text-slate-700 font-medium">
                Dépose le PDF de l'amende ici
              </p>
              <p className="text-xs text-slate-500 mt-1">
                ANTAI · Police municipale · Gendarmerie · Max 10 Mo · PDF/PNG/JPG
              </p>
              <label className="inline-block mt-4">
                <input id="fine-file-input" name="fine-file-input" type="file" accept=".pdf,image/*" className="hidden"
                  onChange={(e) => onFile(e.target.files?.[0])}
                  data-testid="fine-file-input" />
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-md text-sm cursor-pointer hover:bg-slate-800">
                  <FileText className="w-4 h-4" /> Parcourir…
                </span>
              </label>
            </div>
            {file && (
              <div className="p-3 border rounded-md bg-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <FileText className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">{file.name}</span>
                  <span className="text-slate-500">
                    · {(file.size / 1024).toFixed(0)} Ko
                  </span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setFile(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}

        {step === 2 && detected && (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto text-sm">
            <Alert className="bg-blue-50 border-blue-200">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <AlertDescription className="text-xs text-blue-800">
                IA — confiance {(detected.confidence * 100).toFixed(0)}% ·
                Vérifie et corrige si besoin, puis valide.
              </AlertDescription>
            </Alert>

            {detected.duplicate && (
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <AlertDescription className="text-xs text-amber-800">
                  ⚠ Une amende avec la référence <strong>{detected.duplicate.reference}</strong> existe déjà (statut : {detected.duplicate.status}).
                </AlertDescription>
              </Alert>
            )}

            {/* Match véhicule/conducteur */}
            <div className="rounded-md border bg-slate-50 p-3 space-y-3">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-600">
                <Car className="w-3.5 h-3.5" /> AUTO-MATCH IA
              </div>
              {detected.matched_vehicle ? (
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-emerald-600" />
                  <span>Véhicule reconnu : </span>
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200"
                    variant="outline">
                    {detected.matched_vehicle.plate} · {detected.matched_vehicle.brand} {detected.matched_vehicle.model}
                  </Badge>
                </div>
              ) : (
                <div className="text-xs text-amber-700 flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Plaque {detected.plate || "?"} non trouvée dans le parc — sélectionne manuellement.
                </div>
              )}
              <div>
                <Label className="text-[10px] uppercase text-slate-500">Véhicule</Label>
                <select id="wizard-vehicle-select" name="wizard-vehicle-select" className="w-full h-9 border rounded-md px-2 text-sm bg-white"
                  value={selectedVehicleId}
                  onChange={(e) => setSelectedVehicleId(e.target.value)}
                  data-testid="wizard-vehicle-select">
                  <option value="">— Non affecté —</option>
                  {vehicles.map(v =>
                    <option key={v.id} value={v.id}>
                      {v.plate} · {v.brand || ""} {v.model || ""}
                    </option>)}
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-[10px] uppercase text-slate-500">Conducteur affecté</Label>
                  <button type="button"
                    onClick={() => setQuickCreateOpen(true)}
                    className="text-[11px] text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                    data-testid="wizard-quick-create-driver">
                    <UserPlus className="w-3 h-3" /> Créer un conducteur
                  </button>
                </div>
                <select id="wizard-employee-select" name="wizard-employee-select" className="w-full h-9 border rounded-md px-2 text-sm bg-white"
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                  data-testid="wizard-employee-select">
                  <option value="">— Non affecté —</option>
                  {drivers.employee.length > 0 && (
                    <optgroup label="🟢 Salariés">
                      {drivers.employee.map(d => (
                        <option key={d.id} value={d.id}>
                          {d.first_name} {d.last_name}{d.email ? ` · ${d.email}` : ""}{!d.has_license ? " · ⚠ permis" : ""}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {drivers.mandataire_social.length > 0 && (
                    <optgroup label="🟣 Mandataires sociaux">
                      {drivers.mandataire_social.map(d => (
                        <option key={d.id} value={d.id}>
                          {d.first_name} {d.last_name}{d.job_title ? ` · ${d.job_title}` : ""}{!d.has_license ? " · ⚠ permis" : ""}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {drivers.conducteur_externe.length > 0 && (
                    <optgroup label="🟠 Conducteurs externes">
                      {drivers.conducteur_externe.map(d => (
                        <option key={d.id} value={d.id}>
                          {d.first_name} {d.last_name}{!d.has_license ? " · ⚠ permis" : ""}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                {selectedDriver && (
                  <div className="mt-1.5 flex items-center gap-2 text-[11px]">
                    <Badge variant="outline"
                      className={PERSON_TYPE_META[selectedDriver._group].cls}>
                      {PERSON_TYPE_META[selectedDriver._group].label}
                    </Badge>
                    {selectedDriver.has_license ? (
                      <span className="text-emerald-700 flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> Permis renseigné
                      </span>
                    ) : (
                      <span className="text-amber-700 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Permis manquant — à compléter avant Cerfa
                      </span>
                    )}
                  </div>
                )}
                {detected.suggested_employee && detected.suggested_employee.id !== selectedEmployeeId && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Suggéré par le véhicule : {detected.suggested_employee.first_name} {detected.suggested_employee.last_name}
                  </p>
                )}
              </div>
            </div>

            {/* Données extraites — éditables */}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Référence dossier" value={detected.reference || ""}
                onChange={(v) => setDetected({ ...detected, reference: v })} />
              <Field label="NATINF" value={detected.natinf || ""}
                onChange={(v) => setDetected({ ...detected, natinf: v })} />
              <Field label="Date infraction (ISO)" value={detected.infraction_date || ""}
                onChange={(v) => setDetected({ ...detected, infraction_date: v })} />
              <Field label="Heure" value={detected.infraction_time || ""}
                onChange={(v) => setDetected({ ...detected, infraction_time: v })} />
              <Field label="Plaque" value={detected.plate || ""}
                onChange={(v) => setDetected({ ...detected, plate: v })} />
              <Field label="Lieu" value={detected.location || ""}
                onChange={(v) => setDetected({ ...detected, location: v })} />
              <div className="col-span-2">
                <Field label="Motif de l'infraction" value={detected.reason || ""}
                  onChange={(v) => setDetected({ ...detected, reason: v })} />
              </div>
              <Field label="Amende minorée (€)" type="number" value={detected.amount_reduced ?? ""}
                onChange={(v) => setDetected({ ...detected, amount_reduced: v ? Number(v) : null })} />
              <Field label="Amende forfaitaire (€)" type="number" value={detected.amount_normal ?? ""}
                onChange={(v) => setDetected({ ...detected, amount_normal: v ? Number(v) : null })} />
              <Field label="Amende majorée (€)" type="number" value={detected.amount_late ?? ""}
                onChange={(v) => setDetected({ ...detected, amount_late: v ? Number(v) : null })} />
              <Field label="Points retirés" type="number" value={detected.points_lost ?? ""}
                onChange={(v) => setDetected({ ...detected, points_lost: v ? Number(v) : null })} />
              <Field label="Paiement avant (ISO)" value={detected.payment_deadline || ""}
                onChange={(v) => setDetected({ ...detected, payment_deadline: v })} />
              <Field label="Désignation avant (ISO)" value={detected.designation_deadline || ""}
                onChange={(v) => setDetected({ ...detected, designation_deadline: v })} />
              <Field label="Autorité émettrice" value={detected.issuer || ""}
                onChange={(v) => setDetected({ ...detected, issuer: v })} />
            </div>

            {canNotify && (
              <label className="flex items-start gap-2 p-3 border rounded-md cursor-pointer hover:bg-slate-50">
                <input id="wizard-notify-now" name="wizard-notify-now" type="checkbox" checked={notifyNow}
                  onChange={(e) => setNotifyNow(e.target.checked)}
                  className="mt-1" data-testid="wizard-notify-now" />
                <div>
                  <div className="font-medium text-slate-800 text-sm">Notifier immédiatement le conducteur</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Envoie l'email magic-link "confirme ton permis" à
                    {" "}<strong>{allDrivers.find(d => d.id === selectedEmployeeId)?.email}</strong>
                  </div>
                </div>
              </label>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 2 && (
            <Button variant="outline" onClick={() => setStep(1)}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          {step === 1 && (
            <Button onClick={extract} disabled={!file || extracting}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="wizard-extract-btn">
              {extracting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                           : <Sparkles className="w-4 h-4 mr-2" />}
              Extraire avec l'IA
            </Button>
          )}
          {step === 2 && (
            <Button onClick={apply} disabled={applying}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="wizard-apply-btn">
              {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          : <Check className="w-4 h-4 mr-2" />}
              Créer l'amende
            </Button>
          )}
        </DialogFooter>

        <QuickCreateDriverDialog open={quickCreateOpen}
          onOpenChange={setQuickCreateOpen}
          onCreated={onDriverCreated} />
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <Label className="text-[10px] uppercase text-slate-500">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="h-8 text-sm" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// QuickCreateDriverDialog — création ultra-rapide d'un mandataire/externe
// ---------------------------------------------------------------------------
function QuickCreateDriverDialog({ open, onOpenChange, onCreated }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [personType, setPersonType] = useState("conducteur_externe");
  const [email, setEmail] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setFirstName(""); setLastName(""); setEmail("");
      setJobTitle(""); setPersonType("conducteur_externe"); }
  }, [open]);

  const submit = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("Nom et prénom sont requis");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/hr/drivers/quick-create", {
        first_name: firstName.trim(), last_name: lastName.trim(),
        person_type: personType,
        email: email.trim() || null,
        job_title: jobTitle.trim() || null,
      });
      toast.success(`Conducteur ${firstName} ${lastName} créé`);
      onCreated?.(r.data.driver);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-4 h-4" /> Créer un conducteur
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label className="text-xs uppercase text-slate-500">Type</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <button type="button"
                onClick={() => setPersonType("mandataire_social")}
                className={`px-3 py-2 rounded-md border text-left ${
                  personType === "mandataire_social"
                    ? "bg-violet-50 border-violet-300 text-violet-800"
                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}
                data-testid="quick-driver-type-mandataire">
                <div className="text-xs font-semibold flex items-center gap-1">
                  <Briefcase className="w-3 h-3" /> Mandataire social
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Dirigeant sans contrat (gérant SARL, PDG…)
                </div>
              </button>
              <button type="button"
                onClick={() => setPersonType("conducteur_externe")}
                className={`px-3 py-2 rounded-md border text-left ${
                  personType === "conducteur_externe"
                    ? "bg-orange-50 border-orange-300 text-orange-800"
                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}
                data-testid="quick-driver-type-externe">
                <div className="text-xs font-semibold flex items-center gap-1">
                  <User className="w-3 h-3" /> Conducteur externe
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Prestataire, famille, stagiaire ponctuel
                </div>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase text-slate-500">Prénom *</Label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)}
                autoFocus data-testid="quick-driver-first-name" />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Nom *</Label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)}
                data-testid="quick-driver-last-name" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Email (optionnel)</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Requis pour notifier par magic-link"
              data-testid="quick-driver-email" />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Fonction (optionnel)</Label>
            <Input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)}
              placeholder="Ex : Gérant, prestataire…"
              data-testid="quick-driver-job" />
          </div>
          <p className="text-[11px] text-slate-500">
            Le n° de permis et l'adresse seront demandés au moment de générer un Cerfa.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={busy}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="quick-driver-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                     : <UserPlus className="w-4 h-4 mr-2" />}
            Créer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
